import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { subscribeMilestones, subscribeAccountProjects } from "../../projects/api";
import { Card, StageChip, Empty } from "../../dashboard/primitives";
import { Skeleton, SkeletonList, SkeletonRow } from "../../components/Skeleton";
import { useMinLoading } from "../../lib/useMinLoading";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

const ProjectMilestones = ({ projectId }) => {
  const [milestones, setMilestones] = useState(null);
  useEffect(() => subscribeMilestones(projectId, setMilestones), [projectId]);

  if (milestones === null)
    return (
      <div style={{ display: "grid", gap: 8 }}>
        <Skeleton width="70%" height={14} />
        <Skeleton width="50%" height={14} />
        <Skeleton width="60%" height={14} />
      </div>
    );
  if (milestones.length === 0) return <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>No milestones yet</div>;

  return (
    <div>
      {milestones.map((m) => (
        <div key={m.id} className="fw-stage-row">
          <span className="fw-stage-row__idx">{String((m.order ?? 0) + 1).padStart(2, "0")}</span>
          <span>{m.label}</span>
          <span style={{ fontFamily: "var(--fw-mono)", fontSize: 12, color: "var(--fw-muted)" }}>{fmt(m.amount)}</span>
          <StageChip status={m.status === "paid" ? "paid" : m.status === "invoiced" ? "awaiting_approval" : "pending"} />
        </div>
      ))}
    </div>
  );
};

const ProjectsSection = () => {
  const { profile } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!profile?.accountId) {
      setLoaded(true);
      return;
    }
    return subscribeAccountProjects(profile.accountId, (v) => {
      setProjects(v);
      setLoaded(true);
    });
  }, [profile?.accountId]);

  const showSkeleton = useMinLoading(!loaded);

  if (showSkeleton) {
    return (
      <Card title="Your projects">
        <SkeletonList count={2} ariaLabel="Loading your projects">
          <SkeletonRow cols={3} widths={["1fr", "120px", "100px"]} />
        </SkeletonList>
      </Card>
    );
  }

  if (projects.length === 0) {
    return <Card title="Your projects"><Empty>No active projects</Empty></Card>;
  }

  return (
    <>
      {projects.map((p) => (
        <Card
          key={p.id}
          title={p.title}
          action={
            <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <span style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                {p.type || "—"} · {fmt(p.budgetUsd)}
              </span>
              <StageChip status={p.status === "launched" ? "paid" : p.status === "on-hold" ? "closed" : "in_progress"} />
              <Link to={`/dashboard/projects/${p.id}/tasks`} className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }}>
                Tasks →
              </Link>
            </span>
          }
        >
          {p.summary && <p style={{ color: "var(--fw-muted)", fontSize: 13, margin: 0 }}>{p.summary}</p>}
          <ProjectMilestones projectId={p.id} />
        </Card>
      ))}
    </>
  );
};

export default ProjectsSection;
